/**
 * Upload History (Admin)
 * Full, paginated view of every data upload with per-upload detail and
 * original-file redownload. The Data Upload page's "Recent Uploads" card
 * links here once the list outgrows its sidebar.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { PageHeader, LoadingSkeleton, EmptyState } from '../../components/common';
import UploadDetailDialog from '../../components/admin/UploadDetailDialog';
import { uploadStatusChipSx } from '../../components/admin/uploadUi';
import { useUploadHistoryPaged } from '../../services/queries';
import { downloadUploadFile } from '../../services/admin';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';

const mono = { fontFamily: MONO, fontSize: '0.82rem' };

function UploadHistory() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [detailId, setDetailId] = useState(null);
  const [notice, setNotice] = useState('');

  const { data, isLoading, isError } = useUploadHistoryPaged(rowsPerPage, page * rowsPerPage);
  const items = data?.items || [];
  const total = data?.total ?? 0;

  const handleDownload = async (e, upload) => {
    e.stopPropagation();
    setNotice('');
    try {
      await downloadUploadFile(upload.id, upload.filename);
    } catch (err) {
      setNotice(err?.message === 'file not retained'
        ? `"${upload.filename}" was uploaded before file retention shipped, so the original is not stored.`
        : err?.message || 'Download failed.');
    }
  };

  const numCell = (v, color) => (
    <Box component="span" sx={{ ...mono, color: color || palette.text }}>{(v ?? 0).toLocaleString()}</Box>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="Upload History"
        subtitle="Every data upload with its outcome, row-level issues, and the original file"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Admin', path: '/admin/users' },
          { label: 'Data Upload', path: '/admin/upload' },
          { label: 'History' },
        ]}
      />

      {notice && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              <LoadingSkeleton variant="table" rows={8} />
            </Box>
          ) : isError ? (
            <Alert severity="error" sx={{ m: 2 }}>Could not load the upload history.</Alert>
          ) : total === 0 ? (
            <EmptyState
              variant="dashed"
              icon={<Icon icon="ph:file-xls" width={28} height={28} />}
              title="No uploads yet"
              description="Uploads you run will appear here with their full outcome."
              sx={{ m: 3 }}
            />
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={recipes.headCellSx}>File</TableCell>
                      <TableCell sx={recipes.headCellSx}>Date</TableCell>
                      <TableCell sx={recipes.headCellSx}>Uploaded by</TableCell>
                      <TableCell sx={recipes.headCellSx}>Mode</TableCell>
                      <TableCell sx={recipes.headCellSx}>Status</TableCell>
                      <TableCell align="right" sx={recipes.headCellSx}>Processed</TableCell>
                      <TableCell align="right" sx={recipes.headCellSx}>Inserted</TableCell>
                      <TableCell align="right" sx={recipes.headCellSx}>Updated</TableCell>
                      <TableCell align="right" sx={recipes.headCellSx}>Errors</TableCell>
                      <TableCell align="right" sx={recipes.headCellSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((upload) => (
                      <TableRow
                        key={upload.id}
                        hover
                        tabIndex={0}
                        role="button"
                        aria-label={`View details of ${upload.filename}`}
                        onClick={() => setDetailId(upload.id)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailId(upload.id);
                          }
                        }}
                        sx={{ ...recipes.bodyRowSx, cursor: 'pointer', '&:focus-visible': recipes.focusRingSx }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Icon icon="ph:file-xls" width={18} height={18} color={palette.textSecondary} />
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 260 }}>
                              {upload.filename}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={mono}>{fmtDate(upload.uploadDate)}</Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontSize: '0.82rem' }}>
                            {upload.uploadedBy || '·'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', textTransform: 'capitalize' }}>
                            {upload.mode || 'weekly'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={upload.status} sx={uploadStatusChipSx(palette, upload.status)} />
                        </TableCell>
                        <TableCell align="right">{numCell(upload.recordsProcessed)}</TableCell>
                        <TableCell align="right">{numCell(upload.inserted, palette.accent)}</TableCell>
                        <TableCell align="right">{numCell(upload.updated)}</TableCell>
                        <TableCell align="right">
                          {numCell(upload.errors, upload.errors > 0 ? palette.danger : palette.textMuted)}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              aria-label={`View details of ${upload.filename}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailId(upload.id);
                              }}
                              sx={recipes.ghostIconSx}
                            >
                              <Icon icon="ph:eye" width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download original file">
                            <IconButton
                              size="small"
                              aria-label={`Download original file of ${upload.filename}`}
                              onClick={(e) => handleDownload(e, upload)}
                              sx={{ ...recipes.ghostIconSx, ml: 0.5 }}
                            >
                              <Icon icon="ph:download-simple" width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ ...recipes.tableFooterSx, display: 'flex', justifyContent: 'flex-end', py: 0 }}>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(e, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  sx={recipes.paginationSx}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <UploadDetailDialog uploadId={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

export default UploadHistory;
